// Sentry must be imported first so it instruments the React tree from the start.
import "./sentry";
import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";

// iter147 — Defensive DOM patch for browser auto-translate (Chrome / Edge /
// in-app browsers). Translation rewrites text nodes inside React's tree, so
// when React later tries to ``removeChild`` / ``insertBefore`` on a node that
// got swapped, it throws a hard NotFoundError. We swallow that one specific
// error class so React keeps rendering; the next reconciliation pass fixes
// the DOM cleanly. Industry-standard mitigation used by Facebook/Slack/etc.
(function patchDomForTranslate() {
  if (typeof Node === 'undefined' || Node.prototype.__c2pPatched) return;
  Node.prototype.__c2pPatched = true;
  const origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) {
      // Silently no-op — translate already moved the node.
      if (child.parentNode) {
        try { return child.parentNode.removeChild(child); } catch (e) { console.debug('[caught]', e); /* ignore */ }
      }
      return child;
    }
    return origRemoveChild.call(this, child);
  };
  const origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, refNode) {
    if (refNode && refNode.parentNode !== this) {
      // Reference node was moved by translate — fall back to append.
      return this.appendChild(newNode);
    }
    return origInsertBefore.call(this, newNode, refNode);
  };
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);

// Register service worker for PWA + auto-reload on new deployment.
//
// iter151c — Anti-flicker (Android Chrome PWA reload loop).
//   Old behaviour:
//     • `focus` listener triggered `reg.update()` on every focus event.
//     • `focus` listener triggered `checkAppVersion()` on every focus event.
//     • `controllerchange` reloaded the page; the `refreshing` guard was
//       scoped to the `load` callback, so it RESET after every reload.
//   Android PWAs fire `focus` aggressively (status-bar updates, system
//   overlays, notification surfaces). Each focus chained:
//       focus → reg.update() → new SW headers differ marginally between
//       Cloudflare edges → skipWaiting() → controllerchange → reload()
//       → new page → new `refreshing=false` → loop.
//   Resulting symptom: periodic ~500-700ms full-screen flicker on the
//   installed PWA, no flicker on desktop / iOS browser.
//
//   New rules:
//     • Update-checks are throttled to once per 5 minutes (any source).
//     • `focus` listener replaced by `visibilitychange` (fires only on
//       real backgrounding, not on every system overlay).
//     • SW reload guard is sessionStorage-backed so it survives across
//       reloads — second reload within 30s is blocked → loop dies.
//     • `controllerchange` ignores the FIRST event after a fresh page
//       load (i.e. when there was no prior controller). This is the
//       normal "fresh PWA install" pattern and should NOT reload.
const UPDATE_CHECK_MIN_GAP_MS = 5 * 60 * 1000; // 5 minutes
const SW_RELOAD_GUARD_KEY = '__c2p_sw_reload_at';
const SW_RELOAD_MIN_GAP_MS = 30 * 1000; // 30 seconds
let lastUpdateCheckAt = 0;

function shouldCheckForUpdates() {
  const now = Date.now();
  if (now - lastUpdateCheckAt < UPDATE_CHECK_MIN_GAP_MS) return false;
  lastUpdateCheckAt = now;
  return true;
}

function recentlyReloadedBySw() {
  try {
    const at = parseInt(sessionStorage.getItem(SW_RELOAD_GUARD_KEY) || '0', 10);
    return at && (Date.now() - at) < SW_RELOAD_MIN_GAP_MS;
  } catch (e) { console.debug('[caught]', e); return false; }
}

function markSwReload() {
  try { sessionStorage.setItem(SW_RELOAD_GUARD_KEY, String(Date.now())); }
  catch (e) { console.debug('[caught]', e); }
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // If we arrived here WITHOUT an existing controller, this is a fresh
    // PWA load — the upcoming controllerchange is expected and must NOT
    // trigger a reload (that's the canonical reload-loop trigger).
    const hadControllerOnLoad = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.register('/sw.js').then(reg => {
      const checkForUpdate = () => {
        if (!shouldCheckForUpdates()) return;
        reg.update().catch((e) => console.debug('[caught]', e));
      };
      // Only re-check on a real tab/app switch (visibility flip), not on
      // every focus pulse. Throttled to 5min above.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // First controllerchange after a no-controller load == fresh install
        // / SW activation on first run. Never reload — would loop.
        if (!hadControllerOnLoad) return;
        // Guard against a recent reload — if we just reloaded < 30s ago,
        // bail. This kills any residual loop introduced by edge-cache
        // header variation on /sw.js.
        if (recentlyReloadedBySw()) return;
        markSwReload();
        window.location.reload();
      });
    }).catch((e) => console.debug('[caught]', e));
  });
}

// Lightweight client-side build-version poller. Independent of the SW.
// Fixes the "some users see old theme" class of bugs after deploy.
const APP_VERSION_KEY = '__ngal_app_version';
async function checkAppVersion() {
  if (!shouldCheckForUpdates()) return;
  try {
    const r = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
    if (!r.ok) return;
    const { version } = await r.json();
    const prev = localStorage.getItem(APP_VERSION_KEY);
    if (!prev) {
      localStorage.setItem(APP_VERSION_KEY, version);
      return;
    }
    if (prev !== version) {
      localStorage.setItem(APP_VERSION_KEY, version);
      if (recentlyReloadedBySw()) return; // already mid-reload
      markSwReload();
      if ('caches' in window) {
        await Promise.all((await caches.keys()).map(k => caches.delete(k)));
      }
      window.location.reload();
    }
  } catch (e) { console.debug('[caught]', e); /* offline or server restarting — ignore */ }
}
// Initial check on load; re-check ONLY on visibility flip (not every focus
// pulse). Throttled to 5 minutes by `shouldCheckForUpdates`.
window.addEventListener('load', checkAppVersion);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') checkAppVersion();
});
